import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Space, Typography, Tag, Statistic, Table, message, Spin, Progress } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const Insights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/insights');
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const chipColor = (state, failures) => {
    if (state === 'HEALTHY' || (!state && failures === 0)) return 'green';
    if (state === 'DEGRADED' || failures > 0 && failures < 5) return 'orange';
    if (state === 'BANNED' || failures >= 5) return 'red';
    return 'default';
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ margin: 0 }}><BarChartOutlined /> Insights do negócio</Title>
      <Text type="secondary">Métricas agregadas: cohort, LTV, frequência, engajamento, taxa confirmação, saúde dos chips.</Text>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>}

      {data && (
        <>
          {/* Engajamento — números principais */}
          <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
            <Col xs={12} md={5}>
              <Card><Statistic title="Times ATIVOS" value={data.engagement.active_total} /></Card>
            </Col>
            <Col xs={12} md={5}>
              <Card><Statistic title="Trial" value={data.engagement.trial_count}
                suffix={<Text type="secondary" style={{ fontSize: 12 }}>de {data.engagement.active_total}</Text>} /></Card>
            </Col>
            <Col xs={12} md={5}>
              <Card><Statistic title="Ativos WA <7d" value={data.engagement.wa_active_7d}
                suffix={`(${data.engagement.wa_active_7d_pct}%)`} valueStyle={{ color: '#52c41a' }} /></Card>
            </Col>
            <Col xs={12} md={5}>
              <Card><Statistic title="Com grupo sync" value={data.engagement.with_group}
                suffix={`(${data.engagement.with_group_pct}%)`} /></Card>
            </Col>
            <Col xs={12} md={4}>
              <Card><Statistic title="Confirm rate 30d" value={data.confirmation_rate.pct} suffix="%"
                valueStyle={{ color: data.confirmation_rate.pct >= 60 ? '#52c41a' : '#faad14' }} /></Card>
            </Col>
          </Row>

          {/* Cohort retencao */}
          <Card title="📊 Cohort de retenção (últimos 18 meses)" size="small" style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
              Do total cadastrado em cada mês, quantos ainda estão ATIVO hoje.
            </Text>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={[...(data.cohort || [])].reverse()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" unit="%" domain={[0, 100]} />
                <RTooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="signed_up" name="Cadastros" fill="#8884d8" />
                <Bar yAxisId="left" dataKey="still_active" name="Ainda ativos" fill="#52c41a" />
                <Line yAxisId="right" type="monotone" dataKey="retention_pct" name="Retenção %" stroke="#ff4d4f" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* Top LTV */}
          <Card title="💰 Top 20 LTV (times que mais pagaram)" size="small" style={{ marginBottom: 16 }}>
            <Table
              rowKey="customer_id"
              dataSource={data.top_ltv}
              pagination={false}
              size="small"
              columns={[
                { title: '#', width: 40, render: (_, __, i) => i + 1 },
                { title: 'Time', dataIndex: 'game_name',
                  render: (v, r) => <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer">{v || '(sem nome)'}</a> },
                { title: 'Status', dataIndex: 'status', width: 130,
                  render: v => <Tag color={v === 'ATIVO' ? 'green' : v === 'CANCELADO' ? 'red' : 'orange'}>{v}</Tag> },
                { title: 'Plano', dataIndex: 'plan_name', width: 140 },
                { title: 'Pagamentos', dataIndex: 'paid_count', width: 100, align: 'center' },
                { title: 'LTV', dataIndex: 'ltv', width: 130, align: 'right',
                  render: v => <Text strong style={{ color: '#52c41a' }}>R$ {Number(v).toFixed(2)}</Text> },
                { title: '1º pgto', dataIndex: 'first_paid_at', width: 100,
                  render: v => v ? dayjs(v).format('DD/MM/YY') : '—' },
                { title: 'Último pgto', dataIndex: 'last_paid_at', width: 100,
                  render: v => v ? dayjs(v).format('DD/MM/YY') : '—' },
              ]}
            />
          </Card>

          {/* Frequência + Chip health */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={14}>
              <Card title="⚽ Top 30 frequência de jogos (últimos 90d)" size="small">
                <Table
                  rowKey="customer_id"
                  dataSource={data.top_frequency}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  columns={[
                    { title: '#', width: 40, render: (_, __, i) => i + 1 },
                    { title: 'Time', dataIndex: 'game_name',
                      render: (v, r) => <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer">{v || '(sem nome)'}</a> },
                    { title: 'Jogos 90d', dataIndex: 'games_90d', width: 90, align: 'center' },
                    { title: 'Média/mês', dataIndex: 'games_per_month', width: 100, align: 'center',
                      render: v => <Text strong>{v}</Text> },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} md={10}>
              <Card title="📱 Chips (WhatsApp instances)" size="small">
                <Table
                  rowKey="instance_name"
                  dataSource={data.chip_health}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'Chip', dataIndex: 'instance_name',
                      render: v => <Text style={{ fontSize: 11 }}>{v}</Text> },
                    { title: 'Times', dataIndex: 'active_teams', width: 80, align: 'center',
                      sorter: (a, b) => a.active_teams - b.active_teams,
                      defaultSortOrder: 'descend' },
                    { title: 'Estado', dataIndex: 'chip_state', width: 100, align: 'center',
                      render: (v, r) => <Tag color={chipColor(v, r.failures)}>{v || 'unknown'}</Tag> },
                    { title: 'Falhas', dataIndex: 'failures', width: 60, align: 'center' },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          {/* Confirmation rate detail */}
          <Card title="📅 Taxa de confirmação dos jogadores (últimos 30d)" size="small">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Progress percent={data.confirmation_rate.pct} strokeColor="#52c41a"
                  format={p => `${p}% confirmam`} size={['default', 30]} />
                <Space direction="vertical" style={{ marginTop: 12 }}>
                  <Text><Tag color="green">Confirmados</Tag> {data.confirmation_rate.confirmed}</Text>
                  <Text><Tag color="red">Não vão</Tag> {data.confirmation_rate.declined}</Text>
                  <Text><Tag>Sem resposta</Tag> {data.confirmation_rate.no_response}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>Base: {data.confirmation_rate.total} interações • {data.confirmation_rate.teams_analyzed} times</Text>
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={[
                    { label: 'Confirmados', v: data.confirmation_rate.confirmed, fill: '#52c41a' },
                    { label: 'Não vão', v: data.confirmation_rate.declined, fill: '#ff4d4f' },
                    { label: 'Sem resposta', v: data.confirmation_rate.no_response, fill: '#999' },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <RTooltip />
                    <Bar dataKey="v" name="Interações">
                      {[0,1,2].map(i => null)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Col>
            </Row>
          </Card>
        </>
      )}
    </div>
  );
};

export default Insights;
